import { notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useCallback } from 'react';

import type { AutomationConfig } from '../../services/automation';
import type { DatabaseInfo } from '../../services/database-discovery';
import {
  deletesWrittenSourceRows,
  isSourceRowTable,
} from '../automation-input-requirements';
import { type RowWriteState, setRowWriteMode } from './row-write';

/**
 * Adds, removes and sets the automation's output tables (the mappings of a
 * replaced or removed table go with it). Shared by the sidebar's output step
 * and the settings panel's target setting.
 *
 * The source table is no target while status tracking deletes processed
 * rows: the row a run has just written would be deleted with it. Chosen as
 * a target, it starts with the processed row being updated.
 */
export function useOutputTableActions(
  config: AutomationConfig,
  onUpdate: (updates: Partial<AutomationConfig>) => void
) {
  const t = useI18n();

  // sl-3nk: Handle adding a new output table
  const addOutputTable = useCallback(() => {
    // Don't add if we already have tables without selection
    const currentTables = config.outputTables ?? [];
    if (currentTables.some(table => !table.tableId)) return;

    onUpdate({
      outputTables: [
        ...currentTables,
        { tableId: '', tableName: '', docId: '' },
      ],
    });
  }, [config.outputTables, onUpdate]);

  // sl-optc: Handle removing an output table. Removing the last one leaves
  // an automation that writes nothing back.
  const removeOutputTable = useCallback(
    (index: number) => {
      const currentTables = config.outputTables ?? [];

      // Remove the table at index
      const newTables = currentTables.filter((_, i) => i !== index);

      // Remove mappings for the removed table
      const removedTableId = currentTables[index]?.tableId;
      const newMappings = removedTableId
        ? (config.outputMappings ?? []).filter(
            m => m.tableId !== removedTableId
          )
        : (config.outputMappings ?? []);

      onUpdate({
        outputTables: newTables,
        outputMappings: newMappings,
      });
    },
    [config.outputTables, config.outputMappings, onUpdate]
  );

  // sl-3nk: Handle updating a specific output table
  const updateOutputTable = useCallback(
    (index: number, database: DatabaseInfo) => {
      if (
        deletesWrittenSourceRows({
          sourceTableId: config.sourceTableId,
          statusTracking: config.statusTracking,
          triggerConfig: config.triggerConfig,
          outputTables: [{ tableId: database.blockId }],
        })
      ) {
        notify.warning({
          title: database.databaseTitle,
          message:
            t['com.streamlain.automation.sidebar.target-deletes-source'](),
        });
        return;
      }
      if (database.blockId === config.sourceTableId) {
        notify.warning({
          title: database.databaseTitle,
          message:
            t['com.streamlain.streamlain.output.source-overwrite-warning'](),
        });
      }
      const currentTables = config.outputTables ?? [];
      const newTables = [...currentTables];
      const oldTableId = currentTables[index]?.tableId;
      const tableChanged = oldTableId !== database.blockId;
      // Updating the processed row belongs to the source table only.
      const { mode: previousMode, ...current } = currentTables[index] ?? {};
      newTables[index] = {
        ...current,
        ...(!tableChanged && previousMode ? { mode: previousMode } : {}),
        tableId: database.blockId,
        tableName: database.databaseTitle,
        docId: database.docId,
      };

      // Clear mappings for old table if it changed
      let newMappings = config.outputMappings ?? [];
      if (oldTableId && tableChanged) {
        newMappings = newMappings.filter(m => m.tableId !== oldTableId);
      }

      const written: RowWriteState = {
        matchConfigs: config.matchConfigs ?? [],
        outputTables: newTables,
      };
      const next =
        tableChanged &&
        isSourceRowTable(
          {
            sourceTableId: config.sourceTableId,
            triggerConfig: config.triggerConfig,
          },
          { tableId: database.blockId }
        )
          ? setRowWriteMode(written, database.blockId, 'source', [])
          : written;

      onUpdate({
        outputTables: next.outputTables,
        outputMappings: newMappings,
        ...(next.matchConfigs === written.matchConfigs
          ? {}
          : { matchConfigs: next.matchConfigs }),
      });
    },
    [
      config.outputTables,
      config.outputMappings,
      config.matchConfigs,
      config.sourceTableId,
      config.statusTracking,
      config.triggerConfig,
      onUpdate,
      t,
    ]
  );

  return { addOutputTable, removeOutputTable, updateOutputTable };
}
